import React from 'react';
import PropTypes from 'prop-types';
import BaseClass from 'components/BaseClass';
import { wrapComponent } from 'utils/framework';
import Carousel from 'components/Carousel';
import { CARD_GAP, CARD_WIDTH } from 'constants/productCard';
import { Text, Box } from 'components/ui';
import Storage from 'utils/localStorage/Storage';
import contentConsts from 'constants/content';
import { sendCmsComponentEvent } from 'analytics/utils/cmsComponents';
import anaConsts from 'analytics/constants';
import RichText from 'components/Content/RichText';

const {
    RECAP_CAROUSEL: { ITEM_URLS },
    CUSTOM_ACTION_TYPES
} = contentConsts;
const {
    CMS_COMPONENT_EVENTS: { ITEM_CLICK }
} = anaConsts;

class Recap extends BaseClass {
    controlReplenImpressionFired = false;
    controlAnonymousImpressionFired = false;

    componentDidMount() {
        const { requiredData = {} } = this.props;

        this.props.fetchPurchaseHistory(this.props);
        this.props.fetchBeautyRecommendations(this.props);
        requiredData.rvData && this.props.updateRvData(Storage.local.getItem('rvData'));
        this.fetchRtcoDataIfEnabled();
        this.maybeFireControlReplenImpression();
        this.maybeFireControlAnonymousImpression();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.userId !== this.props.userId) {
            this.props.fetchPurchaseHistory(this.props);
            this.props.fetchBeautyRecommendations(this.props);
            this.fetchRtcoDataIfEnabled();
            this.controlReplenImpressionFired = false;
            this.controlAnonymousImpressionFired = false;
        }

        this.maybeFireControlReplenImpression();
        this.maybeFireControlAnonymousImpression();

        if (prevProps.isAnonymous && !this.props.isAnonymous) {
            this.props.clearRecapAbTestData();
        }
    }
    maybeFireControlReplenImpression() {
        if (this.controlReplenImpressionFired) {
            return;
        }

        if (this.props.hasControlReplenImpression && this.props.triggerControlReplenImpression) {
            this.props.triggerControlReplenImpression();
            this.controlReplenImpressionFired = true;
        }
    }
    // PUWLO Unrecognized — control-group impression fires once per session
    // even though the carousel is hidden. Mirrors maybeFireControlReplenImpression
    // so anonymous Control bucket measurement matches Challenger.
    maybeFireControlAnonymousImpression() {
        if (this.controlAnonymousImpressionFired) {
            return;
        }

        if (this.props.hasControlAnonymousImpression && this.props.triggerControlAnonymousImpression) {
            this.props.triggerControlAnonymousImpression();
            this.controlAnonymousImpressionFired = true;
        }
    }

    fetchRtcoDataIfEnabled() {
        const basketItem = this.props?.items?.find(item => item?.action?.targetUrl === ITEM_URLS.BASKET);

        if (basketItem) {
            const { personalization } = basketItem;
            const isEnabled = personalization?.isEnabled;
            const isRtcoEnabled = personalization?.isRtcoEnabled;
            const context = personalization?.context;

            if (isEnabled && isRtcoEnabled && context) {
                this.props.fetchRtcoP13nData({ contextId: context });
            }
        }
    }

    handleAnonymousSubtitleClick = e => {
        if (e.target.getAttribute('role') !== 'button') {
            return;
        }

        const { anonymousSubtitle, title, sid } = this.props;
        const entries = anonymousSubtitle?.text?.links?.entries?.inline || [];
        const isSignInAction = entries.some(entry => entry?.action?.actionType === CUSTOM_ACTION_TYPES.SIGN_IN);

        if (isSignInAction) {
            sendCmsComponentEvent({
                items: [{ ...anonymousSubtitle, itemIndex: 0 }],
                eventName: ITEM_CLICK,
                title,
                sid,
                component: anonymousSubtitle?.type
            });
        }
    };

    render() {
        const {
            sid = null,
            cards = null,
            title = null,
            subtitle = null,
            hideRow = false,
            isLoading = true,
            triggerRecapImpression = null,
            isAnonymous = false,
            anonymousSubtitle = null
        } = this.props;

        if (hideRow) {
            return null;
        }

        return (
            <div id={sid}>
                {title && (
                    <Text
                        is='h2'
                        lineHeight='tight'
                        marginBottom={subtitle || anonymousSubtitle?.text ? 1 : 4}
                        fontSize={['md', 'lg']}
                        fontWeight='bold'
                        children={title}
                    />
                )}
                {subtitle && (
                    <Text
                        is='p'
                        lineHeight='tight'
                        marginBottom={4}
                        marginRight='auto'
                        children={subtitle}
                    />
                )}
                {isAnonymous && anonymousSubtitle?.text && (
                    <Box
                        marginBottom={2}
                        onClickCapture={this.handleAnonymousSubtitleClick}
                    >
                        <RichText content={anonymousSubtitle?.text} />
                    </Box>
                )}
                <Carousel
                    isLoading={isLoading}
                    gap={CARD_GAP}
                    paddingY={4}
                    marginX='-container'
                    scrollPadding={[2, 'container']}
                    itemWidth={CARD_WIDTH}
                    items={cards}
                    hasShadowHack={true}
                    onImpression={triggerRecapImpression}
                />
            </div>
        );
    }
}

Recap.propTypes = {
    sid: PropTypes.string,
    cards: PropTypes.array,
    title: PropTypes.string,
    subtitle: PropTypes.string,
    hideRow: PropTypes.bool,
    isLoading: PropTypes.bool,
    componentList: PropTypes.array,
    items: PropTypes.array,
    fetchRtcoP13nData: PropTypes.func,
    fetchPurchaseHistory: PropTypes.func,
    fetchBeautyRecommendations: PropTypes.func,
    updateRvData: PropTypes.func,
    requiredData: PropTypes.object,
    triggerRecapImpression: PropTypes.func,
    hasControlReplenImpression: PropTypes.bool,
    triggerControlReplenImpression: PropTypes.func,
    hasControlAnonymousImpression: PropTypes.bool,
    triggerControlAnonymousImpression: PropTypes.func,
    isAnonymous: PropTypes.bool,
    anonymousSubtitle: PropTypes.object,
    clearRecapAbTestData: PropTypes.func
};

export default wrapComponent(Recap, 'Recap', true);
